import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FaEdit, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { classAPI, courseAPI } from '../../../../utils/api';
import ClassCreate from '../../create/js/classCreate';
import DataTable from '../../../../components_shared/DataTable';
import TableToolbar from '../../../../components_shared/TableToolbar';

export default function ClassTable({ onCreate, reloadKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [editRow, setEditRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cl, co] = await Promise.all([classAPI.getAll(), courseAPI.getAll()]);
      const coursesMap = {};
      (co.data || []).forEach(x => { coursesMap[x.id] = x; });
      
      const list = (Array.isArray(cl.data) ? cl.data : []).map(r => {
        const courseArr = r.Courses || r.courses || [];
        return {
          ...r,
          Courses: courseArr,
          _courseNames: courseArr.length ? courseArr.map(x => x?.name || `#${x?.id}`).join(', ') : '-',
        };
      });
      setRows(list);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [reloadKey]);

  const remove = async (id) => {
    const res = await Swal.fire({
      title: 'Delete Class?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#e74c3c',
    });
    if (res.isConfirmed) {
      await classAPI.delete(id);
      await load();
      Swal.fire({ icon: 'success', title: 'Deleted', toast: true, position: 'top-end', timer: 3000 });
    }
  };

  const exportCsv = async () => {
    try {
      const res = await classAPI.exportCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'classes_export.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { Swal.fire({ icon:'error', title:'Export failed', text:e.message }); }
  };

  const importCsv = async (csv) => {
    const res = await classAPI.importCsv(csv);
    await load();
    return res.data;
  };

  const downloadSample = async () => {
    try {
      const res = await classAPI.sampleCsv();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'classes_sample.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { 
      accessorKey: 'name', 
      header: 'Class Name', 
      cell: ({ getValue }) => <span style={{ fontWeight: 700, color: '#0f6fad' }}>{getValue()}</span> 
    },
    { 
      accessorKey: '_courseNames', 
      header: 'Assigned Courses',
      cell: ({ getValue }) => <span style={{ fontSize: '0.9rem', color: '#636e72' }}>{getValue()}</span>
    },
    { 
      accessorKey: 'bag_cost', 
      header: 'Est. Bag Cost', 
      cell: ({ getValue }) => getValue() ? `Rs. ${getValue()}` : <span style={{ color: '#ccc' }}>N/A</span>
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn edit" onClick={() => setEditRow(row.original)}><FaEdit /></button>
          <button className="eduss-global-action-btn delete" onClick={() => remove(row.original.id)}><FaTrash /></button>
        </div>
      ),
    },
  ];

  const filteredData = rows.filter(r => 
    !filter || 
    r.name?.toLowerCase().includes(filter.toLowerCase()) ||
    r._courseNames?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar 
        title="Classes Management"
        subtitle="Manage academic grade levels and textbook distribution groups"
        onAdd={onCreate}
        onExport={exportCsv}
        onImport={importCsv}
        onDownloadSample={downloadSample}
        onSearch={setFilter}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
          enableSelection={true}
        />
      </div>

      {editRow && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setEditRow(null)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Update Class</h3>
              <button onClick={() => setEditRow(null)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <ClassCreate classItem={editRow} onClose={() => setEditRow(null)} onSubmit={async () => { await load(); setEditRow(null); }} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

ClassTable.propTypes = {
  onCreate: PropTypes.func,
  reloadKey: PropTypes.number
};
